import { FC, useCallback } from 'react';

import {
    AGENCY_FORM_CONFIG,
    AGREEMENT_TYPE,
    DISABLED_REQUISITES_AFTER_FILLED,
    FINAL_ADVERTISER_FORM_CONFIG,
    FINAL_ADVERTISER_FORM_CONFIG_WITH_AGREEMENT_TYPE,
    FINAL_COUNTERPARTY_TYPE,
    FinalCounterpartyType,
    PRIMARY_AGENCY_FORM_CONFIG,
    RequisiteAgreementType,
    RequisiteName,
    RequisiteRole,
    RequisitesFieldName,
    RequisitesFormName,
    ROLE,
} from 'utils/constants/requisites';

import { Loader, Text, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import Consent from 'components/RequisitesFormModal/Consent/Consent';
import { RequisitesForm } from 'components/RequisitesFormModal/RequisitesForm/RequisitesForm';
import { RequisitesFormInfo } from 'components/RequisitesFormModal/RequisitesFormInfo/RequisitesFormInfo';
import RequisitesRadioField from 'components/RequisitesFormModal/RequisitesRadioField/RequisitesRadioField';

import {
    clearRequisitesForm,
    setInitialInRequisitesForm,
    setRequisiteByName,
    setRequisitesFormByName,
    setRequisitesFormFieldByName,
} from 'store/models/requisites';
import { AccountType } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

interface RequisitesFormModalContentProps {
    isRequisitesEditable: boolean;
    isLoading: boolean;
}

const TrlKeys = {
    finalAdvertiserForm: {
        title: 'RequisitesForm.finalAdvertiserForm.title',
        subtitle: 'RequisitesForm.finalAdvertiserForm.subtitle',
    },
    agencyRequisitesForm: {
        title: 'RequisitesForm.agencyRequisites.title',
        subtitle: 'RequisitesForm.agencyRequisites.subtitle',
    },
    primaryAgencyRequisitesForm: {
        title: 'RequisitesForm.primaryAgencyRequisites.title',
        subtitle: 'RequisitesForm.primaryAgencyRequisites.subtitle',
    },
    tooltip: {
        role: {
            part1: 'RequisitesForm.tooltip.role.part1',
            part2: 'RequisitesForm.tooltip.role.part2',
        },
    },
};

export const RequisitesFormModalContent: FC<RequisitesFormModalContentProps> = ({
    isRequisitesEditable,
    isLoading,
}) => {
    const trl = useTrls();
    const dispatch = useDispatch();

    const existAdvertisingCampaigns = useSelector((state) => state.existAdvertisingCampaigns);
    const { isRequisitesFilled, requisites } = useSelector((state) => state.requisites);
    const accountType = useSelector((state) => state.user.user?.currentAccount.type);

    const isFinalAdvertiser = requisites.isAgency === RequisiteRole.FinalAdvertiser;
    const isAgency = requisites.isAgency === RequisiteRole.Agency;
    const isAgreement = !!requisites.isAgreementDirect;
    const isAgreementNotDirect = requisites.isAgreementDirect === RequisiteAgreementType.NotDirect;
    const isGovernmetAccount = accountType === AccountType.Government;

    const onChangeRole = (fieldName: RequisitesFieldName, value: string) => {
        if (value === RequisiteRole.Agency) {
            dispatch([
                setInitialInRequisitesForm(RequisitesFormName.AgencyRequisites),
                setInitialInRequisitesForm(RequisitesFormName.FinalAdvertiserRequisites),
            ]);
        } else {
            dispatch([
                setRequisiteByName({ fieldName: RequisitesFieldName.IsAgreementDirect, value: null }),
                setRequisitesFormFieldByName({
                    requisiteFormName: RequisitesFormName.FinalAdvertiserRequisites,
                    fieldName: RequisiteName.AgreementType,
                    value: null,
                }),
                clearRequisitesForm(RequisitesFormName.AgencyRequisites),
                clearRequisitesForm(RequisitesFormName.PrimaryAgencyRequisites),
            ]);
        }
    };

    const onChangeFinalCounterpartyType = (fieldName: RequisitesFieldName, value: string) => {
        dispatch(
            setRequisitesFormByName({
                fieldName: RequisitesFieldName.FinalCounterpartyType,
                value: value as FinalCounterpartyType,
            })
        );
    };

    const onChangeAgreementType = (fieldName: RequisitesFieldName, value: string) => {
        if (value === RequisiteAgreementType.NotDirect) {
            dispatch(setInitialInRequisitesForm(RequisitesFormName.PrimaryAgencyRequisites));
        } else {
            dispatch(clearRequisitesForm(RequisitesFormName.PrimaryAgencyRequisites));
        }
    };

    const isDisabledField = useCallback(
        (fieldName: RequisiteName | RequisitesFieldName) => {
            return (
                !isRequisitesEditable ||
                (existAdvertisingCampaigns &&
                    isRequisitesFilled &&
                    DISABLED_REQUISITES_AFTER_FILLED.includes(fieldName))
            );
        },
        [isRequisitesEditable, isRequisitesFilled, existAdvertisingCampaigns]
    );

    const finalAdvertiserForm = (
        <RequisitesForm
            requisiteFormName={RequisitesFormName.FinalAdvertiserRequisites}
            config={
                isAgency && !isGovernmetAccount
                    ? FINAL_ADVERTISER_FORM_CONFIG_WITH_AGREEMENT_TYPE
                    : FINAL_ADVERTISER_FORM_CONFIG
            }
            showPayersSelect={!isAgency}
            isDisabledField={isDisabledField}
            title={trl(TrlKeys.finalAdvertiserForm.title)}
            subtitle={trl(TrlKeys.finalAdvertiserForm.subtitle)}
        />
    );

    const agencyRequisitesForm = (
        <RequisitesForm
            requisiteFormName={RequisitesFormName.AgencyRequisites}
            config={AGENCY_FORM_CONFIG}
            showPayersSelect={isAgency}
            isDisabledField={isDisabledField}
            title={trl(TrlKeys.agencyRequisitesForm.title)}
            subtitle={trl(TrlKeys.agencyRequisitesForm.subtitle)}
        />
    );

    const primaryAgencyRequisitesForm = (
        <RequisitesForm
            requisiteFormName={RequisitesFormName.PrimaryAgencyRequisites}
            config={PRIMARY_AGENCY_FORM_CONFIG}
            isDisabledField={isDisabledField}
            title={trl(TrlKeys.primaryAgencyRequisitesForm.title)}
            subtitle={trl(TrlKeys.primaryAgencyRequisitesForm.subtitle)}
        />
    );

    return (
        <div data-qa="requisites-form-modal">
            <RequisitesFormInfo isRequisitesEditable={isRequisitesEditable} />
            <VSpacing default={32} />
            <div data-qa="requisites-form">
                {isLoading ? (
                    <Loader size={16} color="accent" />
                ) : (
                    <VSpacingContainer default={32}>
                        {!isGovernmetAccount ? (
                            <>
                                <RequisitesRadioField
                                    {...ROLE}
                                    isFieldDisabled={isDisabledField(ROLE.fieldName)}
                                    onChange={onChangeRole}
                                    tooltipContent={
                                        <>
                                            <Text typography="paragraph-2-regular">
                                                {trl(TrlKeys.tooltip.role.part1)}
                                            </Text>
                                            <VSpacing default={10} />
                                            <Text typography="paragraph-2-regular">
                                                {trl(TrlKeys.tooltip.role.part2)}
                                            </Text>
                                        </>
                                    }
                                />
                                {isAgency && (
                                    <RequisitesRadioField
                                        {...AGREEMENT_TYPE}
                                        isFieldDisabled={isDisabledField(AGREEMENT_TYPE.fieldName)}
                                        onChange={onChangeAgreementType}
                                    />
                                )}
                                {isAgency && isAgreement && agencyRequisitesForm}
                                {isAgreementNotDirect && primaryAgencyRequisitesForm}
                                {(isFinalAdvertiser || isAgreement) && finalAdvertiserForm}
                            </>
                        ) : (
                            <>
                                {finalAdvertiserForm}
                                <RequisitesRadioField
                                    {...FINAL_COUNTERPARTY_TYPE}
                                    isFieldDisabled={isDisabledField(FINAL_COUNTERPARTY_TYPE.fieldName)}
                                    onChange={onChangeFinalCounterpartyType}
                                />
                            </>
                        )}
                        {(isFinalAdvertiser || (isAgency && isAgreement) || isGovernmetAccount) && (
                            <Consent isFieldDisabled={isDisabledField(RequisitesFieldName.Consent)} />
                        )}
                    </VSpacingContainer>
                )}
            </div>
        </div>
    );
};
